import React from 'react';
import { PiggyBank } from 'lucide-react';
import LoanPageTemplate from '../../components/LoanPageTemplate';

const PersonalLoans = () => (
  <LoanPageTemplate
    icon={PiggyBank}
    path="/personal-loans"
    title="Personal Loans"
    subtitle="Flexible personal loan options for debt consolidation, renovations, holidays, and major purchases. Compare rates from multiple lenders."
    description="Personal loans offer a structured way to borrow between $2,000 and $75,000 for almost any purpose. Unlike credit cards, personal loans have fixed repayment terms and often lower interest rates, making them a smarter choice for planned expenses. We compare secured and unsecured options from banks, credit unions, and specialist lenders to find the right balance of rate, fees, and flexibility for your situation."
    features={[
      {
        title: 'Debt Consolidation',
        description: 'Combine multiple credit cards, store cards, and small debts into a single personal loan with one lower repayment. We calculate whether consolidation genuinely saves you money after all fees.'
      },
      {
        title: 'Secured vs Unsecured Options',
        description: 'Secured personal loans (backed by a car or term deposit) offer rates from 5.99%, while unsecured loans start around 7.99%. We explain the trade-offs so you choose the right product.'
      },
      {
        title: 'Wedding & Event Finance',
        description: 'The average Australian wedding costs over $35,000. A personal loan can bridge the gap between your savings and your plans, with fixed repayments spread over 2-7 years.'
      },
      {
        title: 'Medical & Dental Finance',
        description: 'Cover the cost of elective surgery, dental implants, IVF, or specialist treatments with a personal loan. Many lenders offer reduced rates for medical purposes with fast approval.'
      },
      {
        title: 'Travel & Holiday Loans',
        description: 'Finance your trip with a fixed-rate personal loan rather than accumulating high-interest credit card debt. Repay over a set term with no revolving balance temptation.'
      },
      {
        title: 'Fast Approval & Same-Day Funding',
        description: 'Many personal loan lenders offer conditional approval within hours and fund settlement within 24-48 hours. We streamline your application to avoid unnecessary delays.'
      }
    ]}
    process={[
      {
        title: 'Purpose & Amount Assessment',
        description: 'We discuss what you need the funds for, how much you require, and your preferred repayment timeline. This helps us narrow down the most suitable products.'
      },
      {
        title: 'Product Comparison',
        description: 'We compare interest rates, comparison rates, fees, and early repayment terms across our lender panel. The comparison rate includes fees so you see the true cost of each option.'
      },
      {
        title: 'Application Submission',
        description: 'We prepare and submit your application with supporting documents. Most personal loan assessments are automated, so decisions are fast when documentation is complete.'
      },
      {
        title: 'Approval & Funding',
        description: 'Once approved, funds are typically deposited into your nominated account within 1-2 business days. For debt consolidation, the lender can pay your existing debts directly.'
      }
    ]}
    faqs={[
      {
        q: 'What is the difference between a secured and unsecured personal loan?',
        a: 'A secured personal loan uses an asset (usually a vehicle or term deposit) as collateral, which reduces the lender\'s risk and results in a lower interest rate. An unsecured loan has no collateral requirement but typically charges a higher rate. If you default on a secured loan, the lender can repossess the asset. We help you weigh the rate saving against the security requirement.'
      },
      {
        q: 'Is it better to consolidate debt with a personal loan or a balance transfer credit card?',
        a: 'Balance transfer cards offer 0% introductory rates but revert to high rates (often 20%+) after the promotional period. Personal loans have a fixed rate for the entire term, making budgeting predictable. If you can repay the full balance within the promotional period, a balance transfer may be cheaper. If not, a personal loan may be a more predictable choice.'
      },
      {
        q: 'How much can I borrow with a personal loan?',
        a: 'Most lenders offer personal loans from $2,000 to $75,000, with loan terms from 1 to 7 years. The amount you qualify for depends on your income, existing debts, living expenses, and credit history. We assess your borrowing capacity upfront so you know exactly what is achievable.'
      },
      {
        q: 'Can I repay my personal loan early without penalty?',
        a: 'Most variable rate personal loans allow unlimited extra repayments and early payout without fees. Fixed rate personal loans may charge an early termination fee or break cost. We prioritise products with flexible repayment terms if early payout is important to you.'
      },
      {
        q: 'Will applying for a personal loan affect my ability to get a home loan later?',
        a: 'Yes, any existing debt reduces your borrowing capacity for a home loan. Lenders assess personal loan repayments as a committed expense. If you plan to apply for a mortgage within 6-12 months, we discuss the timing and whether it makes sense to defer the personal loan or structure it to minimise the impact.'
      }
    ]}
    loanType="Personal Loan"
    color="from-pink-600 to-pink-800"
  />
);

export default PersonalLoans;
